import React, {
	useEffect,
	useRef,
	useState
} from 'react';

import { fetchPatientSearchList } from '../../services/apis/dispense/patientSearch';
import { useAllSelector } from '../../services/selectors/allSelector';
// import { useAuthSelector } from '../../services/selectors/authSelector';
import { clearAllPatientSearchData } from '../../services/slices/dispense/patientSearch';

import ThemeTextInput from '../form/textInput';
import { reportUnexpectedError } from '../../utils/helper/reportError';

import * as Icons from '../../assets/svg/icons';
import * as Hooks from '../../hooks';
import * as routeNames from '../../routes/routeName'

const SearchBox = ({ pharmacyId }) => {

	const currentTimeout = useRef();

	const dispatch = Hooks.useCustomDispatch();
	const navigate = Hooks.useCustomNavigate();
	const location = Hooks.useCustomLocation();

	const [query, setQuery] = useState("");
	const [show, setShow] = useState(false);

	const { patientSearchListResponse } = useAllSelector();
	const patientSearchData = patientSearchListResponse?.data?.data;
	const isLoading = patientSearchListResponse?.loading;

	const searchPatient = async (q) => {
		if (!pharmacyId) return;
		const params = {
			pharmacy_id: pharmacyId,
			query: q
		};
		try {
			await dispatch(fetchPatientSearchList(params));
		} catch (error) {
			reportUnexpectedError('Patient search failed', error, { query: q, pharmacyId });
		}
	};

	useEffect(() => {
		if (currentTimeout.current)
			clearTimeout(currentTimeout.current);
		currentTimeout.current = setTimeout(() => {
			if (query)
				searchPatient(query);
		}, 1000);

		if (!query) {
			dispatch(clearAllPatientSearchData());
		}
	}, [query]);

	const handlePatientSelect = (id) => {
		const searchParams = new URLSearchParams(location.search);
		searchParams.set('id', id);

		navigate({
			pathname: routeNames?.search_patient_data,
			search: `?${searchParams.toString()}`,
		});
	};

	const LoadingSpinner = () => (
		<svg className="w-4 h-4 text-blue-600 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
			<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
			<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
		</svg>
	);

	return (
		<form className="w-full flex-1 h-full relative">
			<div className="relative">
				<ThemeTextInput
					icon={() => <Icons.SearchIcon />}
					value={query}
					placeholder={isLoading && query ? 'Searching...' : 'Search Patients'}
					onChange={(e) => setQuery(e.target.value)}
					inputClassName="h-[58px] text-[16px]"
					inputProps={{
						sizing: 'lg',
						style: { fontSize: 16, height: 48 },
						onFocus: () => setShow(true),
						onBlur: () => setTimeout(() => setShow(false), 150),
						theme: {
							field: {
								input: {
									colors: {
										gray: 'block w-full border disabled:cursor-not-allowed disabled:opacity-50 border-gray-300 bg-gray-50 text-gray-900 focus:border-cyan-500 focus:ring-cyan-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-cyan-500 dark:focus:ring-cyan-500 p-4 sm:text-base pl-10 rounded-lg',
									},
								},
							},
						},
					}}
					isBottomMargin={false}
				/>
				{isLoading && (
					<div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none flex items-center justify-center" aria-hidden="true">
						<LoadingSpinner />
					</div>
				)}
			</div>

			{show && !isLoading && (
				<div
					id="dropdown"
					className={`absolute mt-1 overflow-auto z-10 w-full transition-all duration-200 ease-out
						${patientSearchData?.length === 0 || (patientSearchData?.length > 0)
							? 'bg-white max-h-52 rounded-lg shadow-lg border border-gray-100'
							: ''} divide-y divide-gray-100`}
				>
					<ul className="py-2 text-[16px] text-gray-700 " aria-labelledby="dropdownDefaultButton">
						{patientSearchData?.length === 0 ? (
							<li className="px-4 py-3 text-[16px] text-gray-500">
								No Patient Found.
							</li>
						) : patientSearchData?.length > 0 ? (
							patientSearchData.map((patient, index) => (
								<li key={patient?.id ?? index}>
									<button
										type="button"
										className="text-left text-[16px] px-4 w-full py-2.5 hover:bg-gray-50  flex items-center justify-between transition-colors duration-150"
										onClick={() => handlePatientSelect(patient.id)}
									>
										{patient?.last_name} {patient?.first_name} {patient?.route && <>- {patient?.route}</>}
										<span className="font-bold text-sm uppercase rounded-full text-white" style={{ color: 'black' }}>
											{patient?.delivery_status === 'pending' || patient?.delivery_status === 'final_missed' || patient?.delivery_status === 'today_missed' ? `[P]` :
												patient?.delivery_status === 'delivered' ? `[D]` :
													patient?.delivery_status === 'missed' ? `[M]` : null}
										</span>
									</button>
								</li>
							))
						) : null}
					</ul>
				</div>
			)}
		</form>
	);
}

export default SearchBox;
